import React, { useCallback, useMemo, useState } from "react"
import { Box, Container, Grid, Stack, Typography } from "@mui/material"
import { useRouter } from "next/router"
import { v4 as uuidv4 } from "uuid"
import LendingLogo from "./LendingLogo"
import useTrans from "@/hooks/useTrans"
import useMobileDevice from "@/hooks/useMobileDevice"
import Image from "next/image"
import CardCarousel from "@/components/base/CardCarousel"

import RelendLogo from "@/assets/icons/relend-light.svg"
import UnderstandingSupplyToEarnEn from "@/assets/images/understanding-supply-to-earn.png"
import UnderstandingNFTVoucherEn from "@/assets/images/understanding-nft-voucher.png"
import UnderstandingRelendTokenEn from "@/assets/images/understanding-rel-token.png"
import UnderstandingStakingEn from "@/assets/images/understanding-staking.png"

const CardItem = ({
  index,
  title,
  description,
  items,
  note,
}: {
  index: number
  title: string
  description: string
  items: string[]
  note: string
}) => {
  return (
    <Stack
      alignItems='start'
      justifyContent='space-between'
      className='h-full p-5 mt-4 sm:mt-0 sm:p-0 sm:pb-10 sm:pr-10'
    >
      <Stack gap={0.5}>
        <Box className='relative'>
          <Typography className='font-bold font-montserrat text-[2.5rem] sm:text-[2.5rem] text-primary'>
            {String(index + 1).padStart(2, "0")}
          </Typography>
        </Box>
        <Typography className='text-neutral-950 text-xl sm:text-2xl font-bold'>{title}</Typography>
        <Typography className='text-neutral-600 text-sm sm:text-base'>{description}</Typography>
      </Stack>
      {items.length > 0 && (
        <Stack className='flex-1 w-full mt-4 py-6 sm:py-4 border-t border-solid border-secondary'>
          {items.map((item, index) => (
            <Stack direction='row' alignItems='self-start' key={index} className='text-neutral-900 mb-2' gap={1}>
              <Typography className='font-semibold'>{index + 1}.</Typography>
              <Typography dangerouslySetInnerHTML={{ __html: item }} className='flex-1 font-semibold' />
            </Stack>
          ))}
        </Stack>
      )}
      <Typography dangerouslySetInnerHTML={{ __html: note }} />
    </Stack>
  )
}

const UnderstandingRenecLend = () => {
  const trans = useTrans()
  const [currTab, setCurrentTab] = useState(0)
  const isMobile = useMobileDevice()
  const { locale } = useRouter()

  const handleTabClick = useCallback((tab: number) => {
    setCurrentTab(tab)
  }, [])

  const imageDetails = useMemo(() => {
    const isEng = locale === "en"
    return [
      isEng ? UnderstandingSupplyToEarnEn : UnderstandingSupplyToEarnEn,
      isEng ? UnderstandingNFTVoucherEn : UnderstandingNFTVoucherEn,
      isEng ? UnderstandingRelendTokenEn : UnderstandingRelendTokenEn,
      isEng ? UnderstandingStakingEn : UnderstandingStakingEn,
    ]
  }, [locale])

  const tabDetail = trans.home.understanding_details.details[currTab]

  return (
    <Box className='w-full bg-[#FBFCFA] py-10 sm:py-20'>
      <Container maxWidth='xl'>
        <LendingLogo />

        <Stack alignItems='center' gap={1}>
          <Typography className='text-neutral-1000 text-center font-montserrat text-2xl sm:text-[2.5rem] font-bold leading-tight'>
            {trans.home.understanding_renec_lend}
          </Typography>
          <Typography className='text-sm sm:text-base text-neutral-600 text-center sm:max-w-3xl'>
            {trans.home.discover_the_key_functions}
          </Typography>
        </Stack>

        <Box className='w-full border border-solid border-secondary rounded-2xl mt-10 mb-6 relative'>
          <Image
            className='absolute top-0 right-0 sm:h-52 sm:w-52 z-0'
            src={RelendLogo}
            alt='relend'
            width={75}
            height={79}
          />

          {!isMobile ? (
            <Grid container spacing={4} className='mt-0'>
              <Grid item md={6} sm={12} xs={12} className='md:mt-0 md:pl-20 md:pr-0 sm:pl-16 sm:pr-10 sm:pt-10'>
                <Image
                  src={imageDetails[currTab]}
                  alt='understanding-renec-lend-image'
                  className='m-auto'
                  unoptimized
                />
              </Grid>
              <Grid item md={6} sm={12} xs={12} className='pt-14 md:pl-10 sm:pl-20 z-10'>
                <CardItem index={currTab} {...tabDetail} />
              </Grid>
            </Grid>
          ) : (
            <CardCarousel
              id='understanding-renec-lend'
              xs={1}
              sm={1}
              md={2}
              items={trans.home.understanding_details.details.map((item: any, index: number) => (
                <Stack key={uuidv4()}>
                  <Image src={imageDetails[index]} alt='understanding-renec-lend-image' />
                  <CardItem index={index} {...item} />
                </Stack>
              ))}
            />
          )}
        </Box>
        <Grid container spacing={3} className='hidden sm:flex'>
          {trans.home.understanding_details.tabs.map((item: { title: string; description: string }, index: number) => (
            <Grid key={uuidv4()} item xs={12} md={3} sm={3}>
              <Stack
                className={`py-3 cursor-pointer border-t-4 border-solid ${
                  currTab === index ? "border-primary" : "border-secondary"
                }`}
                gap={0.5}
                onClick={() => handleTabClick(index)}
              >
                <Typography className={`text-lg font-bold ${currTab === index ? "text-primary" : "text-neutral-1000"}`}>
                  {String(index + 1).padStart(2, "0")}
                </Typography>
                <Typography className={`font-semibold ${currTab === index ? "text-primary" : "text-neutral-1000"}`}>
                  {item.title}
                </Typography>
                <Typography className='text-neutral-600 text-sm'>{item.description}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default UnderstandingRenecLend
